import { alpha, createTheme, Theme, ThemeProvider, useTheme } from '@mui/material/styles';
import { deepmerge } from '@mui/utils';
import { useContext } from 'react';
import { ExtendedThemeOptions, MAEDefaultThemeProvider, ThemeOptionsContext } from './mae-default-theme-provider';
import type { ThemeProviderProps } from './theme-types';
import { getDarkLightMode } from './utils';

export const FORM_ROW_HEIGHT = 40;
// Most table rows contain buttons, making them taller than standard small table rows.
// To ensure consistency between small tables with and without icons, we set a minimum height of 28px.
export const TABLE_SMALL_CELL_MIN_HEIGHT = 28;

function StudioThemeProviderComponent({ children }: ThemeProviderProps): JSX.Element {
	const outerThemeOptions = useContext(ThemeOptionsContext);
	const theme = useTheme();

	const innerThemeOptions = deepmerge(outerThemeOptions, {
		components: {
			MuiTableCell: {
				styleOverrides: {
					sizeSmall: {
						minHeight: TABLE_SMALL_CELL_MIN_HEIGHT
					}
				}
			},
			MuiOutlinedInput: {
				styleOverrides: {
					notchedOutline: {
						borderColor: alpha(theme.palette.text.primary, getDarkLightMode() === 'dark' ? 0.38 : 0.23)
					},
					root: {
						'&.Mui-error .MuiOutlinedInput-notchedOutline': {
							borderColor: theme.palette.error.main
						},
						'&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
							borderColor: alpha(theme.palette.text.primary, getDarkLightMode() === 'dark' ? 0.3 : 0.2)
						},
						'&.Mui-disabled.Mui-error .MuiOutlinedInput-notchedOutline': {
							borderColor: theme.palette.error.main
						}
					}
				}
			},
			MuiTextField: {
				defaultProps: {
					variant: 'outlined',
					size: 'small',
					color: 'secondary'
				},
				styleOverrides: {
					root: {
						'& .MuiInputBase-sizeSmall:not(.MuiInputBase-multiline)': {
							// Height is manually set to remain consistent with other components used alongside text fields (e.g., icons).
							// We cannot set the height globally because it would have side effects on other components outside the form.
							height: FORM_ROW_HEIGHT
						}
					}
				}
			},
			MuiSelect: {
				defaultProps: {
					variant: 'outlined',
					size: 'small',
					color: 'secondary'
				}
			},
			MuiIconButton: {
				styleOverrides: {
					root: {
						'&.form-icon-button': {
							width: FORM_ROW_HEIGHT,
							height: FORM_ROW_HEIGHT,
							padding: 'unset'
						}
					}
				}
			},
			MuiSkeleton: {
				styleOverrides: {
					root: {
						'&.form-skeleton-field': {
							height: FORM_ROW_HEIGHT
						}
					}
				}
			}
		}
	} as ExtendedThemeOptions);

	const studioThemeClient: Theme = createTheme(innerThemeOptions);

	return (
		<ThemeOptionsContext.Provider value={innerThemeOptions}>
			<ThemeProvider theme={studioThemeClient}>{children}</ThemeProvider>
		</ThemeOptionsContext.Provider>
	);
}
export function StudioClientThemeProvider({ children }: ThemeProviderProps): JSX.Element {
	return (
		<MAEDefaultThemeProvider>
			<StudioThemeProviderComponent>{children}</StudioThemeProviderComponent>
		</MAEDefaultThemeProvider>
	);
}
